import { UserAgentApplication } from "msal"
import {createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { azureConfig, scopes } from '../azure/azureConfig'
import { loginWithPassword, storeSession, clearStoredSession } from '../azure/simpleLogin'

const userAgentApplication = new UserAgentApplication(azureConfig)

export const getUserState = createAsyncThunk(
  'userData/getUserState',
  async () => {
    return userAgentApplication.acquireTokenSilent({scopes})
  }
)

// Staging and local development sign in against the API instead of a tenant. The
// resulting state is deliberately the same shape, so every useSelector on
// state.userData.successToken works without knowing which path produced it.
export const simpleLogin = createAsyncThunk(
  'userData/simpleLogin',
  async ({userName, password}) => {
    const session = await loginWithPassword(userName, password)
    storeSession(session)
    return session
  }
)

const initialState = {
  userName: null,
  userRole: null,
  userEmail: null,
  homeAccountIdentifier: null,
  successToken: null,
  idToken: null,
  status: null,
  error: null,
}

const userSlice = createSlice({
  name: 'userData',

  initialState,
  
  reducers: {
    setNewStatus(state, action) {
      state.status = action.payload
    },

    // Rehydrates from sessionStorage on reload - see App.js.
    setSimpleSession(state, action) {
      const session = action.payload
      state.userName = session.name
      state.userRole = session.role
      state.successToken = session.accessToken
      state.status = 'success'
      state.error = null
    },

    nullifyUserState(state) {
      state.userRole = null
      state.token = null
      state.refreshToken = null
      state.status = null
    },

    setNewState(state, action) {
      const response = action.payload
      const claims = action.payload.idToken.claims

      state.userName = response.account.name
      state.userRole = claims.roles[0]
      state.homeAccountIdentifier = response.account.homeAccountIdentifier
      state.successToken = response.accessToken
      state.idToken =  response.idToken.rawIdToken
      state.status = 'success'
      state.error = null
    }
  },

  extraReducers: {
    [getUserState.pending]: (state, action) => {
      state.status = 'loading'
    },

    [getUserState.fulfilled]: (state, action) => {
      const response = action.payload
      const claims = action.payload.idToken.claims

      state.userName = response.account.name
      state.userEmail = response.account.userName
      state.userRole = claims?.roles?.[0]
      state.homeAccountIdentifier = response.account.homeAccountIdentifier
      state.successToken = response.accessToken
      state.idToken = response.idToken.rawIdToken
      state.status = 'success'
      state.error = null
    },

    [getUserState.rejected]: (state, action) => {
      state.status = 'failed'
      state.error = action.error.message
    },

    [simpleLogin.pending]: (state) => {
      state.status = 'loading'
      state.error = null
    },

    [simpleLogin.fulfilled]: (state, action) => {
      const session = action.payload
      state.userName = session.name
      state.userRole = session.role
      state.successToken = session.accessToken
      state.status = 'success'
      state.error = null
    },

    [simpleLogin.rejected]: (state, action) => {
      clearStoredSession()
      state.status = 'failed'
      state.error = action.error.message
    },
  }
})

export default userSlice.reducer
export const { setNewStatus, nullifyUserState, setNewState, setSimpleSession } = userSlice.actions